import { Location } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged } from 'rxjs/operators';
import { FunctionalityCode } from 'src/app/core/_enums/functionality.code';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { ResourceTypeCodes } from 'src/app/core/_enums/resource.type.codes';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Ceu } from 'src/app/core/_models/ceu';
import { Family } from 'src/app/core/_models/family';
import { Transport } from 'src/app/core/_models/transport';
import { Version } from 'src/app/core/_models/version';
import { AuthService } from 'src/app/core/_services/auth.service';
import { FamilyService } from 'src/app/core/_services/family.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { TransportService } from 'src/app/core/_services/transport.service';
import { UploadService } from 'src/app/core/_services/upload.service';
import { VersionService } from 'src/app/core/_services/versions.service';
import { UtilsService } from 'src/app/core/_utils/utils.service';
import { UpdateResourceAction } from 'src/app/store/actions/resource.actions';
import { AppState } from 'src/app/store/app.state';
import { environment } from 'src/environments/environment';
import { ResourceListRefreshService } from 'src/app/core/_services/resource.list.refresh.service';
import { CeuService } from 'src/app/core/_services/ceu.service';
import { DialogDuplicateCodeComponent } from '../shared/dialog-duplicate-code/dialog-duplicate-code.component';
import { DialogLeaveComponent } from '../shared/dialog-leave/dialog-leave.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';

@Component({
  selector: 'app-transport',
  templateUrl: './transport.component.html',
  styleUrls: ['./transport.component.scss']
})
export class TransportComponent implements OnInit {
  unSaved: boolean = false;

  currentItem: Transport = {};
  form!: FormGroup;
  families: Array<Family> = [];
  familyRoutierId: string | undefined;
  perimeterId!: string;
  favoriteId?: string;
  manageTransports!: boolean;
  justificationFilePath: string;
  fileToUpload?: File;
  acuView!: boolean;

  versionId: string = '';
  dbVersions: Version[] = [];

  @ViewChild('fileInput', { static: false })
  fileInput!: ElementRef;

  constructor(
    private activeRoute: ActivatedRoute,
    private transportService: TransportService,
    private store: Store<AppState>,
    private router: Router,
    private snackbarService: SnackbarService,
    public dialog: MatDialog,
    private uploadService: UploadService,
    private familyService: FamilyService,
    private location: Location,
    private utilsService: UtilsService,
    private versionService: VersionService,
    private authService: AuthService,
    private spinnerService: SpinnerOverlayService,
    private resourceListRefreshService: ResourceListRefreshService,
    private translate: TranslateService
  ) {
    this.justificationFilePath = environment.baseUrl + 'Documents/Justification/';

    this.form = new FormGroup({
      resourceId: new FormControl(),
      code: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(9)]),
      displayName: new FormControl('', Validators.required),
      familyId: new FormControl('', Validators.required),
      owner: new FormControl({ value: '', disabled: true }),
      creator: new FormControl({ value: '', disabled: true }),
      description: new FormControl(''),
      energieDescription: new FormControl(''),
      ceu: new FormGroup({
        en: new FormControl('', Validators.required),
        co: new FormControl('', Validators.required)
      }),
      fileName: new FormControl(),
      fileDescription: new FormControl(),
      versionId: new FormControl()
    });
  }

  async canDeactivate(): Promise<boolean> {
    if (this.unSaved) {
      const dialogRef = this.dialog.open(DialogLeaveComponent);

      var rep = await dialogRef.afterClosed().toPromise();
      if (rep) {
        return true;
      }

      return false;
    }
    return true;
  }

  ngDoCheck(): void {
    if (this.authService?.accessesValues) this.manageTransports = this.authService.accessesValues.includes(FunctionalityCode.ManageTransport);
    this.acuView = this.authService.accessesValues.includes(FunctionalityCode.ACUView);
  }

  isFormDisable() {
    return !this.manageTransports || !(this.currentItem?.canUpdate || !this.currentItem?.resourceId);
  }

  onVersionChange(value: string) {
    this.transportService.getCeu(this.currentItem?.resourceId!, value).subscribe((ceu) => {
      let isEmpty: boolean = Object.keys(ceu).length == 0;
      if (isEmpty) {
        this.snackbarService.showMessage(this.translate.instant('error_resource_not_in_version'));
      }

      this.form.patchValue({
        ceu: {
          en: isEmpty ? '' : ceu.en,
          co: isEmpty ? '' : ceu.co
        }
      });
    });
  }

  ngOnInit(): void {
    this.activeRoute.params.subscribe((params) => {
      this.perimeterId = history.state?.perimeterId;

      //Charger la liste des familles
      this.familyService.getRessourceByTypeCode(ResourceTypeCodes.Transport).subscribe((f: Family[]) => {
        if (f.length > 0) {
          this.families = f;
          this.familyRoutierId = f.find((fam) => fam.code === 'ROU')?.familyId;
        }
      });

      if (params?.id && params?.id !== '0') {
        this.versionService.getAll().subscribe((versions) => {
          // Chargement de la liste de toutes les versions
          this.dbVersions = versions;

          this.form.reset();

          // Préselection de la dernière version dans la liste
          this.form.patchValue({
            versionId: versions[0].versionId
          });

          this.transportService.get(params?.id).subscribe(
            (transport) => {
              let vId = transport.ruade ? this.versionId : undefined;

              //Charger les CEU
              this.transportService.getCeu(params?.id, vId).subscribe((ceu: Ceu) => {
                if (ceu) {
                  transport.ceu = ceu;
                  this.form.patchValue(transport);
                  this.currentItem = transport;
                  if (this.currentItem.resourceId) {
                    this.utilsService.getFavoriteIdForResource(this.currentItem.resourceId).then((res) => {
                      this.favoriteId = res ? res.userFavoriteId : '';
                    });
                  }
                  this.unSaved = false;

                  let isEmpty: boolean = Object.keys(ceu).length == 0;
                  if (isEmpty) {
                    this.snackbarService.open(SnackbarTypes.ResourceNotAvailableForversion);
                    /*this.snackbarService.showMessage(
                    "La ressource n'existe pas dans cette version."
                  );*/
                  }
                }
              });
            },
            (error) => {
              this.snackbarService.open(SnackbarTypes.error);
              this.router.navigate([SiteMapUrl.MenuTransport]);
            }
          );
        });
      }

      // Subscribe to form changes, ignoring changes only to versionId and ceu
      this.form.valueChanges
        .pipe(
          distinctUntilChanged((prev, curr) => {
            if (this.isFormDisable()) {
              // Remove versionId and ceu from comparison
              const { versionId: _, ceu: __, ...prevRest } = prev;
              const { versionId: ___, ceu: ____, ...currRest } = curr;

              return JSON.stringify(prevRest) === JSON.stringify(currRest);
            }

            return JSON.stringify(prev) === JSON.stringify(curr);
          })
        )
        .subscribe(() => {
          this.unSaved = true;
        });
    });
  }

  get formCrls() {
    return this.form.controls;
  }

  async clickSave(transport: Transport) {
    if (this.form.invalid) {
      return;
    }

    this.unSaved = false;
    this.fileInput.nativeElement.value = '';

    // INSERT
    if (!this.formCrls?.resourceId?.value) {
      delete transport.resourceId;
      if (!this.currentItem?.ruade) transport.versionId = undefined;
      let rep = this.transportService.add(transport);
      rep.subscribe(
        (data: Transport) => {
          this.currentItem = data;
          this.majStore(SnackbarTypes.add);
          this.location.replaceState(`${SiteMapUrl.MenuTransport}/${data.resourceId}`);
          this.router.navigate([`${SiteMapUrl.MenuTransport}`, data.resourceId]);
        },
        (error) => {
          this.snackbarService.openError(error);
        }
      );
    }
    // UPDATE
    else {
      transport.versionId = this.currentItem?.ruade ? this.formCrls.versionId.value : undefined;

      let rep = await this.transportService.update(transport);
      rep.subscribe(
        (data: Transport) => {
          this.currentItem = data;
          this.majStore(SnackbarTypes.update);
        },
        (error) => {
          this.snackbarService.openError(error);
        }
      );
    }
  }

  clickDuplicate() {
    this.dialog
      .open(DialogDuplicateCodeComponent, { data: { code: this.currentItem.code, maxLength: 9, duplicate: (code: string) => this.transportService.duplicate(this.currentItem.resourceId, code) } })
      .afterClosed()
      .subscribe((data) => {
        if (data !== undefined) this.majStore(SnackbarTypes.duplicate);
      });
  }

  //Supprimer un transport
  openDialogDelete(resourceId?: string): void {
    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title: this.translate.instant('confirm_delete_transport') }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.delete(resourceId);
      }
    });
  }

  delete(resourceId?: string) {
    if (resourceId) {
      this.transportService.delete(resourceId).subscribe({
        next: (data) => {
          this.majStore(SnackbarTypes.delete);
          this.router.navigate([SiteMapUrl.MenuTransport]);
        },
        error: (error) => {
          this.snackbarService.open(SnackbarTypes.error);
          console.error(error);
        }
      });
    }
  }

  majStore(snackbarMsgType: string) {
    this.transportService.getAll(GroupBy.family).subscribe(
      (arr) => {
        if (arr) {
          this.store.dispatch(new UpdateResourceAction(arr));
          this.snackbarService.open(snackbarMsgType);
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
      }
    );

    this.resourceListRefreshService.triggerRefresh();
  }

  uploadProof(resourceId?: string, files?: any) {
    if (!resourceId || !files || files.length === 0) {
      return;
    }

    this.fileToUpload = <File>files[0];
    const formData = new FormData();
    formData.append('file', this.fileToUpload, `${resourceId}.${this.fileToUpload.name.split('.').pop()}`);

    this.spinnerService.show();
    this.uploadService.uploadProof(resourceId, formData).subscribe(
      (event: any) => {
        if (event.type === HttpEventType.Response) {
          this.spinnerService.hide();
          this.form.get('fileDescription')?.setValue(event?.body);
          this.form.get('fileName')?.setValue(this.fileToUpload?.name);
          this.fileInput.nativeElement.value = '';
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    );
  }

  deleteFile() {
    this.fileInput.nativeElement.value = '';
    this.form.get('fileName')?.reset();
    this.form.get('fileDescription')?.reset();
  }

  addToFavorite(resourceId: string) {
    this.utilsService.addToFavorite(resourceId).then((res) => {
      this.favoriteId = res;
    });
  }

  removeFromFavorite() {
    if (this.favoriteId) {
      this.utilsService.removeFromFavorite(this.favoriteId).then((res) => {
        this.favoriteId = '';
      });
    }
  }
}
